import React, { useState, useEffect } from 'react';
import { api } from '../services/apiClient';
import { Swords, Briefcase, RefreshCw, PieChart as PieIcon, Search, Users, ArrowDown, ArrowUp, Repeat, Calendar, DollarSign, Trophy, BarChart3 } from 'lucide-react';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useTranslation } from 'react-i18next';
import ProPaywall from '../components/ProPaywall'; // Import du Paywall

const COLORS = ['#4f46e5', '#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#3b82f6', '#ef4444', '#64748b'];

const ArenaPage = () => {
    const { t } = useTranslation();
    const [gurus, setGurus] = useState([]);
    const [selectedGuruId, setSelectedGuruId] = useState(null);
    const [guruData, setGuruData] = useState(null); 
    const [topHoldings, setTopHoldings] = useState([]);
    const [viewMode, setViewMode] = useState('guru'); 
    
    // NOUVEAU : État pour le statut PRO
    const [isProUser, setIsProUser] = useState(false);
    
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [globalSyncing, setGlobalSyncing] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const fmt = (val) => {
        if (val === undefined || val === null || isNaN(val)) return "$0";
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: "compact" }).format(val);
    };

    useEffect(() => { loadGurus(); }, []);

    const loadGurus = async () => {
        const token = localStorage.getItem('token');
        
        try {
            // Vérification du statut PRO
            if (token) {
                const userRes = await api.get('/auth/me');
                setIsProUser(userRes.data.is_pro);
            }
        } catch (error) {
            console.error("Erreur Auth:", error);
        }

        fetch('/api/gurus/', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => {
                const sorted = d.sort((a, b) => (b.total_value || 0) - (a.total_value || 0));
                setGurus(sorted);
                if (!selectedGuruId && sorted.length > 0) {
                    setSelectedGuruId(sorted[0].id);
                }
            });
    };

    useEffect(() => {
        if (!selectedGuruId || viewMode === 'consensus' || !isProUser) return; // Ne charge les détails que si PRO
        setLoading(true);
        const token = localStorage.getItem('token');
        fetch(`/api/gurus/${selectedGuruId}`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(d => {
                setGuruData(d);
                setLoading(false);
            });
    }, [selectedGuruId, globalSyncing, viewMode, isProUser]);

    const loadTopHoldings = () => {
        setViewMode('consensus');
        if (!isProUser) return; // Ne charge les détails que si PRO
        setLoading(true);
        const token = localStorage.getItem('token');
        fetch('/api/gurus/top-holdings', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(data => {
                setTopHoldings(data);
                setLoading(false);
            });
    };

    const handleSync = async () => {
        setSyncing(true);
        const token = localStorage.getItem('token');
        try {
            await fetch(`/api/gurus/${selectedGuruId}/sync`, { 
                method: 'POST', 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            alert(t('arena.alert_sync'));
            setTimeout(() => window.location.reload(), 3000); 
        } catch (e) { alert(t('arena.alert_error')); } finally { setSyncing(false); }
    };

    const handleGlobalSync = async () => {
        if(!window.confirm(t('arena.alert_sync_all'))) return;
        setGlobalSyncing(true);
        const token = localStorage.getItem('token');
        try {
            await fetch(`/api/gurus/sync/all`, { 
                method: 'POST', 
                headers: { 'Authorization': `Bearer ${token}` } 
            });
            alert(t('arena.alert_sync_all_ok'));
        } catch (e) { alert(t('arena.alert_error')); setGlobalSyncing(false); }
        setTimeout(() => setGlobalSyncing(false), 5000);
    };

    const filteredGurus = gurus.filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-12 font-sans text-slate-900">
            <div className="max-w-7xl mx-auto px-4">
                
                {/* HEADER - Toujours visible pour donner envie */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-8 gap-4">
                    <div>
                        <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                            <Swords className="text-indigo-600" /> {t('arena.title')}
                        </h1>
                        <p className="text-slate-500 mt-2">{t('arena.subtitle')}</p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {/* BOUTON TOP CONSENSUS */}
                        <button 
                            onClick={loadTopHoldings}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold transition-all border ${
                                viewMode === 'consensus'
                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <Trophy size={18} className={viewMode === 'consensus' ? "text-yellow-300" : "text-yellow-500"} />
                            {t('arena.btn_consensus')}
                        </button>

                        <button 
                            onClick={handleGlobalSync}
                            disabled={globalSyncing || !isProUser}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold transition-all border ${
                                globalSyncing || !isProUser
                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                                : 'bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50 shadow-sm'
                            }`}
                        >
                            <RefreshCw size={18} className={globalSyncing ? "animate-spin" : ""} />
                            {globalSyncing ? t('arena.btn_syncing') : t('arena.btn_sync_all')}
                        </button>

                        <div className="relative">
                            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input 
                                type="text" placeholder={t('arena.search_ph')} 
                                className="pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    
                    {/* SIDEBAR - Toujours visible pour montrer le catalogue */}
                    <style>{`
                        .no-scrollbar::-webkit-scrollbar { display: none; }
                        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                    `}</style>
                    
                    <div className="lg:col-span-1 space-y-1.5 max-h-[800px] overflow-y-auto pr-1 no-scrollbar">
                        {filteredGurus.map(g => (
                            <button 
                                key={g.id} 
                                onClick={() => { setSelectedGuruId(g.id); setViewMode('guru'); }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all border ${
                                    selectedGuruId === g.id && viewMode === 'guru'
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md transform scale-[1.01]' 
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                }`}
                            >
                                <div className="flex justify-between items-center mb-0.5">
                                    <span className={`font-bold text-sm truncate pr-2 ${selectedGuruId === g.id && viewMode === 'guru' ? 'text-white' : 'text-slate-800'}`}>
                                        {g.name}
                                    </span>
                                    <span className={`text-[11px] font-black ${selectedGuruId === g.id && viewMode === 'guru' ? 'text-indigo-200' : 'text-slate-500'}`}>
                                        {fmt(g.total_value)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className={`text-[11px] truncate max-w-[65%] ${selectedGuruId === g.id && viewMode === 'guru' ? 'text-indigo-200' : 'text-slate-400'}`}>
                                        {g.fund_name}
                                    </span>
                                    <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-[4px] ${
                                        selectedGuruId === g.id && viewMode === 'guru' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500 border border-slate-200'
                                    }`}>
                                        {g.style}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* MAIN CONTENT AREA */}
                    <div className="lg:col-span-3 space-y-6 relative">
                        
                        {/* CONDITION MAGIQUE : Le Paywall couvre la zone principale si non PRO */}
                        {!isProUser ? (
                            <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-sm rounded-3xl">
                                <ProPaywall 
                                    title="L'Arène des Gurus PRO" 
                                    description="Accédez aux portefeuilles détaillés des légendes de l'investissement. Découvrez leurs dernières acquisitions, le consensus de l'Olympe et leurs stratégies." 
                                />
                            </div>
                        ) : null}

                        {/* Contenu - Flouté si non PRO */}
                        <div className={!isProUser ? "opacity-30 pointer-events-none select-none blur-sm transition-all" : ""}>
                            
                            {/* VUE CONSENSUS (TOP 10) */}
                            {viewMode === 'consensus' && (
                                <div className="animate-fade-in">
                                    <div className="bg-gradient-to-br from-indigo-900 to-slate-900 rounded-3xl p-8 border border-indigo-800 shadow-xl relative overflow-hidden text-white mb-6">
                                        <div className="relative z-10">
                                            <h2 className="text-3xl font-black mb-2 flex items-center gap-3">
                                                <Trophy className="text-yellow-400" size={32} />
                                                {t('arena.cons_title')}
                                            </h2>
                                            <p className="text-indigo-200">
                                                {t('arena.cons_desc', { count: gurus.length })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                        {loading ? (
                                            <div className="text-center py-20 text-slate-400">{t('arena.cons_loading')}</div>
                                        ) : (
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left">
                                                    <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs">
                                                        <tr>
                                                            <th className="px-6 py-4 rounded-l-lg">{t('arena.cons_col_rank')}</th>
                                                            <th className="px-6 py-4">{t('arena.col_symbol')}</th>
                                                            <th className="px-6 py-4">{t('arena.col_company')}</th>
                                                            <th className="px-6 py-4 text-center">{t('arena.cons_col_holders')}</th>
                                                            <th className="px-6 py-4 text-right rounded-r-lg">{t('arena.cons_col_invested')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-100">
                                                        {topHoldings.map((h, i) => (
                                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                                <td className="px-6 py-4">
                                                                    <span className={`
                                                                        flex items-center justify-center w-8 h-8 rounded-full font-black text-sm
                                                                        ${i === 0 ? 'bg-yellow-100 text-yellow-600 border border-yellow-200' : 
                                                                          i === 1 ? 'bg-slate-100 text-slate-600 border border-slate-200' : 
                                                                          i === 2 ? 'bg-orange-100 text-orange-600 border border-orange-200' : 'text-slate-400'}
                                                                    `}>
                                                                        {i + 1}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4 font-black text-slate-900">{h.symbol}</td>
                                                                <td className="px-6 py-4 text-slate-600 font-medium">{h.company_name}</td>
                                                                <td className="px-6 py-4 text-center">
                                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs font-bold border border-indigo-100">
                                                                        <Users size={12}/> {h.count} / {gurus.length}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4 text-right font-mono font-bold text-emerald-600">
                                                                    {fmt(h.total_value)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* VUE GURU (DETAIL) */}
                            {viewMode === 'guru' && guruData && (
                                <>
                                    {/* HEADER CARD */}
                                    <div className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-full blur-3xl opacity-50 pointer-events-none"></div>
                                        <div className="relative z-10">
                                            <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-8">
                                                <div>
                                                    <div className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-1">{guruData.guru.style}</div>
                                                    <h2 className="text-3xl font-black text-slate-900 mb-1">{guruData.guru.fund_name}</h2>
                                                    <div className="text-lg text-slate-500 font-medium flex items-center gap-2"><Users size={18}/> {guruData.guru.name}</div>
                                                    
                                                    <div className="mt-4 flex gap-4 text-xs text-slate-400 font-bold uppercase tracking-wider">
                                                        <span>{t('arena.guru_update')} {guruData.guru.last_update ? new Date(guruData.guru.last_update).toLocaleDateString() : t('arena.guru_never')}</span>
                                                    </div>
                                                </div>
                                                
                                                <button onClick={handleSync} disabled={syncing} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all shadow-sm ${syncing ? 'bg-slate-100' : 'bg-white text-indigo-600 border border-indigo-200'}`}>
                                                    <RefreshCw size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? '...' : t('arena.guru_btn_sync')}
                                                </button>
                                            </div>

                                            <div className="flex flex-wrap gap-4">
                                                <div className="flex flex-col items-center p-4 bg-indigo-600 rounded-2xl border border-indigo-600 text-white shadow-lg shadow-indigo-200 min-w-[140px]">
                                                    <span className="text-xs text-indigo-200 font-bold uppercase mb-1 flex items-center gap-1"><DollarSign size={12}/> {t('arena.guru_assets')}</span>
                                                    <span className="text-2xl font-black">{fmt(guruData.guru.total_value)}</span>
                                                </div>

                                                <div className="flex flex-col items-center p-4 bg-white rounded-2xl border border-slate-100 min-w-[120px]">
                                                    <span className="text-xs text-slate-400 font-bold uppercase mb-1 flex items-center gap-1"><PieIcon size={12}/> {t('arena.guru_positions')}</span>
                                                    <span className="text-xl font-black text-slate-700">{guruData.holdings.length}</span>
                                                </div>

                                                <div className="flex flex-col items-center p-4 bg-white rounded-2xl border border-slate-100 min-w-[120px]">
                                                    <span className="text-xs text-slate-400 font-bold uppercase mb-1 flex items-center gap-1"><Repeat size={12}/> {t('arena.guru_turnover')}</span>
                                                    <span className="text-xl font-black text-slate-700">{guruData.guru.turnover}%</span>
                                                </div>
                                                
                                                <div className="flex flex-col items-center p-4 bg-white rounded-2xl border border-slate-100 min-w-[120px]">
                                                    <span className="text-xs text-slate-400 font-bold uppercase mb-1 flex items-center gap-1"><Calendar size={12}/> {t('arena.guru_hold_period')}</span>
                                                    <span className="text-xl font-black text-slate-700">{guruData.guru.avg_holding_period} {t('arena.guru_months')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* HOLDINGS */}
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm min-h-[300px]">
                                            <h3 className="text-sm font-bold text-slate-500 uppercase mb-4 flex items-center gap-2"><PieIcon size={16}/> {t('arena.allocation_title')}</h3>
                                            {guruData.holdings.length > 0 ? (
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <PieChart>
                                                        <Pie data={guruData.holdings.slice(0, 10)} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                                            {guruData.holdings.slice(0, 10).map((entry, index) => ( <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} /> ))}
                                                        </Pie>
                                                        <Tooltip formatter={(val) => fmt(val)} contentStyle={{borderRadius:'8px', border:'none'}} />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : <div className="text-center text-xs text-slate-300 mt-10">{t('arena.allocation_empty')}</div>}
                                        </div>

                                        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                            <h3 className="text-sm font-bold text-slate-500 uppercase mb-4 flex items-center gap-2"><Briefcase size={16}/> {t('arena.holdings_title')}</h3>
                                            {guruData.holdings.length > 0 ? (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left text-sm">
                                                        <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs">
                                                            <tr>
                                                                <th className="px-4 py-3">{t('arena.col_symbol')}</th>
                                                                <th className="px-4 py-3">{t('arena.col_company')}</th>
                                                                <th className="px-4 py-3 text-center">{t('arena.col_type')}</th>
                                                                <th className="px-4 py-3 text-right">{t('arena.col_value')}</th>
                                                                <th className="px-4 py-3 text-right">{t('arena.col_weight')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {guruData.holdings.slice(0, 20).map((h, i) => (
                                                                <tr key={i} className="hover:bg-slate-50">
                                                                    <td className="px-4 py-3 font-black text-slate-800">{h.symbol}</td>
                                                                    <td className="px-4 py-3 text-slate-600 truncate max-w-[150px]">{h.company_name}</td>
                                                                    
                                                                    <td className="px-4 py-3 text-center">
                                                                        {h.position_type === "PUT" && (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-100 text-rose-600 rounded text-[10px] font-bold border border-rose-200">
                                                                                <ArrowDown size={10} /> PUT
                                                                            </span>
                                                                        )}
                                                                        {h.position_type === "CALL" && (
                                                                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-600 rounded text-[10px] font-bold border border-emerald-200">
                                                                                <ArrowUp size={10} /> CALL
                                                                            </span>
                                                                        )}
                                                                        {h.position_type === "SHARE" && (
                                                                            <span className="text-[10px] text-slate-300 font-bold">-</span>
                                                                        )}
                                                                    </td>

                                                                    <td className="px-4 py-3 text-right font-mono font-medium">{fmt(h.value)}</td>
                                                                    <td className="px-4 py-3 text-right font-bold text-slate-700">{h.weight.toFixed(2)}%</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : <div className="text-center py-10 text-slate-400">{t('arena.holdings_empty')}</div>}
                                        </div>
                                    </div>
                                </>
                            )}
                            
                            {!selectedGuruId && !loading && viewMode === 'guru' && (
                                <div className="text-center py-24 text-slate-400">
                                    {t('arena.empty_select')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ArenaPage;
